import { EnvVariable, getEnv } from 'lib/helpers/envHelper';
import { buildQueryParamLink } from 'lib/helpers/urlHelper';
import { UnauthorizedErrorType } from 'lib/requests/types';
import { EthenaQueryParam } from 'lib/types';
import _ from 'lodash';

const SOFT_LOGIN_PARAMS = [
  EthenaQueryParam.LEARNING_CENTER_LOGIN_KEY,
  EthenaQueryParam.SSO,
  EthenaQueryParam.SCORM_USER_LOGIN_KEY,
] as const;

export const NO_ACCESS_ERROR_MESSAGE =
  'You do not have access to this page. If you believe this is an error, please contact your administrator.';

/**
 * Builds a redirect path for the unauthorized error type when attempting to access a resource that requires the user to be soft logged in.
 *
 * @param asPath - The path of the current page.
 * @param errorType - The type of unauthorized error.
 * @returns The redirect path (or `null` if no special redirect is needed.
 */
export const buildUnauthorizedSoftLoginRedirectPath = ({
  asPath,
  errorType,
}: {
  asPath: string;
  errorType: UnauthorizedErrorType;
}): string | null => {
  const { basePath, otherParams, params } = parseRequestedSoftLoginPath(asPath);
  if (
    errorType === UnauthorizedErrorType.LOGIN_KEY_EXPIRED &&
    otherParams.kioskKey
  ) {
    return buildQueryParamLink(`${getEnv(EnvVariable.API_PATHNAME)}/logout`, {
      redirectPath: buildQueryParamLink('/learning/kiosk', {
        kioskKey: otherParams.kioskKey,
      }),
    });
  }

  if (errorType === UnauthorizedErrorType.LINK_UNAVAILABLE) {
    return '/learning/link-unavailable';
  }

  if (
    errorType === UnauthorizedErrorType.AUTH_CONFLICT ||
    errorType === UnauthorizedErrorType.LOGIN_KEY_EXPIRED
  ) {
    return buildQueryParamLink('/learning/choose-your-account', {
      key: params.key,
      path: buildQueryParamLink(basePath, otherParams),
    });
  }

  if (errorType === UnauthorizedErrorType.NO_PROFILE_FOUND) {
    return buildQueryParamLink('/error', {
      msg: NO_ACCESS_ERROR_MESSAGE,
    });
  }

  if (params.sso) {
    return buildQueryParamLink(`${getEnv(EnvVariable.API_PATHNAME)}/login`, {
      redirectPath: buildQueryParamLink(basePath, {
        ...otherParams,
        sso: params.sso,
      }),
    });
  }

  return buildQueryParamLink('/learning/login', {
    path: buildQueryParamLink(basePath, otherParams),
  });
};

interface IParsedSoftLoginRequestedPath {
  /** The base path of the requested path, without any query params (e.g. `/learning/kiosk`). */
  basePath: string;

  /** Any additional params that are not used for soft login. */
  otherParams: Record<string, string>;

  /** The params used for soft login: `key`, `sso`, `scormKey`. */
  params: Partial<Record<(typeof SOFT_LOGIN_PARAMS)[number], string>>;
}

export const parseRequestedSoftLoginPath = (
  path: string
): IParsedSoftLoginRequestedPath => {
  const [basePath, queryString] = _.split(path, '?');
  const searchParams = new URLSearchParams(`?${queryString}`);

  let params: Partial<Record<(typeof SOFT_LOGIN_PARAMS)[number], string>> = {};
  for (const param of SOFT_LOGIN_PARAMS) {
    const value = searchParams.get(param);
    if (value) {
      params[param] = value;
      searchParams.delete(param);
    }
  }

  const otherParams = Object.fromEntries(searchParams.entries());

  return { basePath, otherParams, params };
};
